import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChevronDown, 
  ChevronUp, 
  MapPin, 
  Presentation,
  Upload,
  ClipboardCheck,
  Brain,
  Search,
  Shield,
  Share2,
  Gavel,
  Archive,
  Settings,
  RotateCcw,
  CheckCircle,
  Circle
} from 'lucide-react';

interface DemoStep {
  id: string;
  label: string;
  shortLabel: string;
  icon: typeof Presentation;
  paths: string[];
  personas: string[];
  duration: string;
}

const demoSteps: DemoStep[] = [
  { 
    id: 'intro', 
    label: 'Introduction & Platform Overview', 
    shortLabel: 'Intro',
    icon: Presentation, 
    paths: ['/demo-hub', '/diagrams', '/'],
    personas: ['any'],
    duration: '5 min'
  },
  { 
    id: 'intake', 
    label: 'Evidence Intake', 
    shortLabel: 'Intake',
    icon: Upload, 
    paths: ['/evidence/upload'],
    personas: ['LawEnforcement'],
    duration: '6 min'
  },
  { 
    id: 'approve', 
    label: 'Case Manager Approval', 
    shortLabel: 'Approve',
    icon: ClipboardCheck, 
    paths: ['/evidence/intake'],
    personas: ['CaseManager'],
    duration: '4 min'
  },
  { 
    id: 'ai-analysis', 
    label: 'AI-Powered Analysis', 
    shortLabel: 'AI Analysis',
    icon: Brain, 
    paths: ['/evidence'],
    personas: ['any'],
    duration: '10 min'
  },
  { 
    id: 'review', 
    label: 'Evidence Review & Search', 
    shortLabel: 'Review',
    icon: Search, 
    paths: ['/cases', '/evidence'],
    personas: ['Attorney'],
    duration: '5 min'
  },
  { 
    id: 'redaction', 
    label: 'PII Redaction', 
    shortLabel: 'Redaction',
    icon: Shield, 
    paths: ['/pii-demo'],
    personas: ['Attorney', 'CaseManager'],
    duration: '5 min'
  },
  { 
    id: 'discovery', 
    label: 'Discovery Sharing', 
    shortLabel: 'Discovery',
    icon: Share2, 
    paths: ['/discovery'],
    personas: ['Attorney'],
    duration: '4 min'
  },
  { 
    id: 'courtroom', 
    label: 'Courtroom Presentation', 
    shortLabel: 'Courtroom',
    icon: Gavel, 
    paths: ['/courtroom'],
    personas: ['Attorney'],
    duration: '4 min'
  },
  { 
    id: 'disposition', 
    label: 'Disposition & Retention', 
    shortLabel: 'Disposition',
    icon: Archive, 
    paths: ['/disposition'],
    personas: ['CaseManager'],
    duration: '5 min'
  },
  { 
    id: 'compliance', 
    label: 'Security & Compliance', 
    shortLabel: 'Compliance',
    icon: Settings, 
    paths: ['/compliance', '/audit'],
    personas: ['SystemAdmin'],
    duration: '5 min'
  },
  { 
    id: 'ai-agents', 
    label: 'AI Agents Overview', 
    shortLabel: 'AI Agents',
    icon: Brain, 
    paths: ['/ai-agents'],
    personas: ['SystemAdmin'],
    duration: '5 min'
  },
  { 
    id: 'reset', 
    label: 'Demo Reset', 
    shortLabel: 'Reset',
    icon: RotateCcw, 
    paths: ['/demo-hub'],
    personas: ['SystemAdmin'],
    duration: '2 min'
  },
];

export function DemoFlowNavigator() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const location = useLocation();
  useAuth(); // Ensures component re-renders on auth changes

  // Find current step based on path
  const currentStepIndex = demoSteps.findIndex(step => 
    step.paths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'))
  );

  const currentStep = currentStepIndex >= 0 ? demoSteps[currentStepIndex] : null;

  // Calculate progress
  const progressPercent = currentStepIndex >= 0 
    ? Math.round(((currentStepIndex + 1) / demoSteps.length) * 100)
    : 0;

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-40 p-3 bg-justice-navy text-white rounded-full shadow-lg hover:bg-justice-navy/90 transition-all group"
        title="Show demo navigator"
      >
        <MapPin className="h-5 w-5" />
        <span className="absolute -top-1 -right-1 h-5 w-5 bg-justice-gold text-justice-navy text-xs font-bold rounded-full flex items-center justify-center">
          {currentStepIndex + 1}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-40 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-justice-navy to-justice-navy/90 text-white px-4 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-justice-gold" />
            <span className="text-sm font-semibold">Demo Flow</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(true);
              }}
              className="text-white/70 hover:text-white text-xs"
            >
              minimize
            </button>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2 h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-justice-gold rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-white/70">
          <span>Step {currentStepIndex + 1} of {demoSteps.length}</span>
          <span>{progressPercent}%</span>
        </div>
      </div>

      {/* Current Step - Always visible */}
      {currentStep && (
        <div className="px-4 py-3 bg-justice-gold/10 border-b border-justice-gold/20">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span className="uppercase tracking-wide font-medium">You are here</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-justice-gold/20 rounded-lg">
              <currentStep.icon className="h-5 w-5 text-justice-navy" />
            </div>
            <div>
              <p className="font-semibold text-justice-navy text-sm">{currentStep.shortLabel}</p>
              <p className="text-xs text-gray-500">{currentStep.duration}</p>
            </div>
          </div>
        </div>
      )}

      {/* Expanded step list */}
      {isExpanded && (
        <div className="max-h-64 overflow-y-auto">
          {demoSteps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const Icon = step.icon;

            return (
              <div 
                key={step.id}
                className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 last:border-0 ${
                  isCurrent ? 'bg-blue-50' : ''
                }`}
              >
                <div className={`flex-shrink-0 ${
                  isCompleted ? 'text-green-500' : 
                  isCurrent ? 'text-justice-navy' : 
                  'text-gray-300'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Circle className={`h-4 w-4 ${isCurrent ? 'fill-current' : ''}`} />
                  )}
                </div>
                <Icon className={`h-4 w-4 ${
                  isCompleted ? 'text-green-500' :
                  isCurrent ? 'text-justice-navy' :
                  'text-gray-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${
                    isCompleted ? 'text-green-700' :
                    isCurrent ? 'text-justice-navy' :
                    'text-gray-500'
                  }`}>
                    {step.shortLabel}
                  </p>
                </div>
                <span className={`text-xs ${
                  isCurrent ? 'text-justice-navy font-medium' : 'text-gray-400'
                }`}>
                  {step.duration}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Suggested next persona */}
      {currentStep && currentStepIndex < demoSteps.length - 1 && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            <span className="font-medium">Next:</span>{' '}
            {demoSteps[currentStepIndex + 1].shortLabel}
            {demoSteps[currentStepIndex + 1].personas[0] !== 'any' && (
              <span className="ml-1 text-justice-navy">
                ({demoSteps[currentStepIndex + 1].personas[0]})
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

export default DemoFlowNavigator;
